import React, { useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { authService } from '../../../../services/api';
import { LoginErrorResponse } from '../../../../types/auth';
import SchoolIcon from '@mui/icons-material/School';

const PageContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `
        linear-gradient(135deg, 
            ${alpha(theme.palette.primary.dark, 0.2)} 0%, 
            ${alpha(theme.palette.secondary.dark, 0.2)} 50%,
            ${alpha(theme.palette.primary.main, 0.15)} 100%
        ),
        radial-gradient(
            circle at 20% 20%,
            ${alpha(theme.palette.primary.main, 0.15)} 0%,
            transparent 50%
        ),
        radial-gradient(
            circle at 80% 80%,
            ${alpha(theme.palette.secondary.main, 0.15)} 0%,
            transparent 50%
        )
    `,
    padding: theme.spacing(2),
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            radial-gradient(circle at 50% 50%, 
                ${alpha(theme.palette.primary.main, 0.08)} 0%,
                transparent 70%
            ),
            repeating-linear-gradient(
                45deg,
                ${alpha(theme.palette.common.black, 0.02)} 0px,
                ${alpha(theme.palette.common.black, 0.02)} 1px,
                transparent 1px,
                transparent 10px
            )
        `,
        pointerEvents: 'none',
    },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: theme.spacing(3),
    background: `
        linear-gradient(145deg, 
            ${alpha(theme.palette.background.paper, 0.85)} 0%,
            ${alpha(theme.palette.background.paper, 0.75)} 100%
        ),
        linear-gradient(45deg,
            ${alpha(theme.palette.primary.light, 0.05)} 0%,
            ${alpha(theme.palette.secondary.light, 0.05)} 100%
        )
    `,
    boxShadow: `
        0 10px 30px ${alpha(theme.palette.common.black, 0.15)},
        0 1px 3px ${alpha(theme.palette.common.black, 0.1)},
        inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.1)},
        inset 0 0 20px ${alpha(theme.palette.primary.main, 0.05)}
    `,
    backdropFilter: 'blur(10px)',
    border: `
        1px solid ${alpha(theme.palette.common.white, 0.2)},
        1px solid ${alpha(theme.palette.primary.main, 0.1)}
    `,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '6px',
        background: `
            linear-gradient(90deg, 
                ${theme.palette.primary.main} 0%,
                ${theme.palette.secondary.main} 50%,
                ${theme.palette.primary.main} 100%
            )
        `,
        opacity: 0.8,
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            radial-gradient(
                circle at 50% 0%,
                ${alpha(theme.palette.primary.main, 0.08)} 0%,
                transparent 70%
            )
        `,
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: `
            0 20px 40px ${alpha(theme.palette.common.black, 0.2)},
            0 1px 3px ${alpha(theme.palette.common.black, 0.1)},
            inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.2)},
            inset 0 0 30px ${alpha(theme.palette.primary.main, 0.08)}
        `,
        '&::after': {
            background: `
                radial-gradient(
                    circle at 50% 0%,
                    ${alpha(theme.palette.primary.main, 0.12)} 0%,
                    transparent 70%
                )
            `,
        },
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
        borderRadius: theme.spacing(2),
    },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const LogoIcon = styled(SchoolIcon)(({ theme }) => ({
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.1)',
        filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.4)})`,
    },
}));

const Form = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        transition: 'all 0.3s ease',
        '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
        },
        '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
            },
        },
    },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontSize: '1.1rem',
    fontWeight: 600,
    background: `
        linear-gradient(45deg,
            ${theme.palette.primary.main} 0%,
            ${theme.palette.primary.dark} 100%
        )
    `,
    boxShadow: `
        0 4px 14px ${alpha(theme.palette.primary.main, 0.4)},
        0 1px 2px ${alpha(theme.palette.common.black, 0.1)}
    `,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        background: `
            linear-gradient(45deg,
                ${theme.palette.primary.dark} 0%,
                ${theme.palette.primary.main} 100%
            )
        `,
        boxShadow: `
            0 6px 20px ${alpha(theme.palette.primary.main, 0.6)},
            0 1px 3px ${alpha(theme.palette.common.black, 0.15)}
        `,
    },
}));

const LanguageButton = styled(Button)(({ theme }) => ({
    position: 'absolute',
    top: 16,
    right: 16,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    backdropFilter: 'blur(8px)',
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
    '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 1),
    },
}));

interface LoginFormProps {
    onLoginSuccess: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            await authService.login({ email, password });
            onLoginSuccess();
        } catch (err) {
            const error = err as LoginErrorResponse;
            setError(error.message || t('auth.login.errors.invalidCredentials'));
        } finally {
            setLoading(false);
        }
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
        document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    };

    return (
        <PageContainer>
            <Container component="main" maxWidth="xs">
                <StyledPaper elevation={3}>
                    <LanguageButton
                        onClick={toggleLanguage}
                        variant="outlined"
                        size="small"
                    >
                        {t('common.language')} ({i18n.language.toUpperCase()})
                    </LanguageButton>

                    <LogoContainer>
                        <LogoIcon />
                        <Typography component="h1" variant="h4" fontWeight="bold" color="primary">
                            {t('auth.login.title')}
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary" align="center">
                            {t('auth.login.subtitle')}
                        </Typography>
                    </LogoContainer>

                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                width: '100%', 
                                mt: 2,
                                borderRadius: 1,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <StyledTextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label={t('auth.login.email')}
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                            error={!!error}
                            disabled={loading}
                        />
                        <StyledTextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label={t('auth.login.password')}
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                            error={!!error}
                            disabled={loading}
                        />
                        <SubmitButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            size={isMobile ? 'large' : 'medium'}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t('auth.login.submit')
                            )}
                        </SubmitButton>
                    </Form>
                </StyledPaper>
            </Container>
        </PageContainer>
    );
}; 